import { ClickAwayListener, Stack } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers-pro";
import { isValid } from "date-fns";
import deepEqual from "fast-deep-equal";
import { useState } from "react";
import { formatDateTime } from "@entrilia/shared/utilities/dateUtils";
import type { ValidationResult } from "@entrilia/shared/utilities/validators";
import { invalidResult, validResult } from "@entrilia/shared/utilities/validators";
import type { FieldAttribute } from "../../../api/types/objectTypes";
import { useFieldValuesContext } from "../FieldValuesContext";
import { findNextEntityInput, findPreviousEntityInput } from "../entityFieldNavigator";
import FieldValueWrapper from "./FieldValueWrapper";

interface Props {
  value: Date | null;
  fieldAttributes: FieldAttribute[];
  onChange: (newValue: Date | null, validationResult: ValidationResult) => void;
  fieldInputId?: string;
}

interface DateValidationResult extends ValidationResult {
  showValidation?: boolean;
}

const EntityDateTimeFieldValueComponent = ({ value, fieldAttributes, onChange, fieldInputId }: Props) => {
  const [date, setDate] = useState(value);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [validationResult, setValidationResult] = useState<DateValidationResult>(validResult);
  const { updateFieldValuesState } = useFieldValuesContext();

  const handleDateChange = (date: Date | null) => {
    if (date && !isValid(date)) {
      const invalidDateResult = invalidResult("Invalid date");
      setValidationResult(invalidDateResult);
      onChange(null, invalidDateResult);
      return;
    }

    if (fieldAttributes.includes("Required") && date === null) {
      const requiredInvalidResult = invalidResult("Required field. Please select the necessary date.");
      setValidationResult(requiredInvalidResult);
      onChange(null, requiredInvalidResult);
    } else {
      setValidationResult(validResult());
    }

    setDate(date);
  };

  const [prevValue, setPrevValue] = useState(value);
  if (!deepEqual(value, prevValue)) {
    setPrevValue(value);
    if (value !== undefined) {
      setDate(value || null);
    }
  }

  const handleValueOnLeave = () => {
    if (!validationResult.isValid) {
      setValidationResult({ ...validationResult, showValidation: true });
      return;
    }

    if (!deepEqual(date, value)) {
      onChange(date, validationResult);
    } else {
      updateFieldValuesState({ fieldEditState: "viewing", isValid: true });
    }

    setIsEdit(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleValueOnLeave();
      return;
    }

    if (e.key === "Tab" && fieldInputId) {
      const nextElement = e.shiftKey ? findPreviousEntityInput(fieldInputId) : findNextEntityInput(fieldInputId);
      if (nextElement) {
        e.preventDefault();
        nextElement.click();
      }
      return;
    }
  };

  const handleClickAway = (event: MouseEvent | TouchEvent) => {
    const targetNode = event.target as Node;

    const openCalendar = document.querySelector(".MuiDateCalendar-root");
    if (targetNode && openCalendar && openCalendar.contains(targetNode)) {
      return;
    }

    const openTimePicker = document.querySelector(".MuiMultiSectionDigitalClock-root");
    if (targetNode && openTimePicker && openTimePicker.contains(targetNode)) {
      return;
    }

    handleValueOnLeave();
  };

  return (
    <Stack direction="row">
      {isEdit && (
        <ClickAwayListener onClickAway={handleClickAway}>
          <DateTimePicker
            autoFocus
            disabled={fieldAttributes.includes("ReadOnly")}
            value={date}
            onChange={handleDateChange}
            slotProps={{
              field: {
                clearable: true,
              },
              textField: {
                error: !validationResult.isValid && validationResult.showValidation,
                helperText: validationResult.showValidation ? validationResult.error : "",
                slotProps: { input: { sx: { width: 240 } } },
                onKeyDown: handleKeyDown,
              },
            }}
          />
        </ClickAwayListener>
      )}

      {!isEdit && (
        <FieldValueWrapper
          id={fieldInputId}
          isReadonly={fieldAttributes.includes("ReadOnly")}
          displayValue={date ? formatDateTime(date) : ""}
          isEdit={!validationResult.isValid}
          onEditChange={setIsEdit}
        />
      )}
    </Stack>
  );
};

export default EntityDateTimeFieldValueComponent;
